import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  ArrowRight,
  ArrowDown,
  MapPin,
  ShoppingBag,
  Users,
  MessageSquare,
  Fuel,
  Crown,
  Instagram,
  Music2,
  Globe,
  Youtube,
  DollarSign,
  Mail,
  Sparkles,
} from "lucide-react";
import { SectionAnchor, RoadBetween, ScrollMileIndicator } from "@/components/road/RoadSpine";
import { StatesGrid, StatesLegend } from "@/components/road/StatesGrid";
import { CommunityForm } from "@/components/road/CommunityForms";
import { BucketListItems, BucketListSuggestionForm } from "@/components/road/BucketList";
import { SafeImage } from "@/lib/image";

/* --------------------------------------------------------------------------
 *  Constants
 * ------------------------------------------------------------------------- */
const BRAND_LOGO =
  "https://customer-assets.emergentagent.com/job_journey-hub-283/artifacts/6ld7fx8l_file_00000000ae8871f5b0415f16ffc8ce51~2.png";

// Journey launch — Jan 11, 2027 (per creator spec).
const LAUNCH_DATE = "2027-01-11T00:00:00Z";

/* --------------------------------------------------------------------------
 *  Countdown
 * ------------------------------------------------------------------------- */
function launchCountdown(nowMs = Date.now()) {
  const diff = new Date(LAUNCH_DATE).getTime() - nowMs;
  if (diff <= 0) return { status: "live" };
  return {
    status: "counting",
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    mins: Math.floor((diff % 3_600_000) / 60_000),
    secs: Math.floor((diff % 60_000) / 1000),
  };
}
const pad = (n) => String(n).padStart(2, "0");

function Countdown() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const c = useMemo(() => launchCountdown(now), [now]);
  if (c.status === "live") {
    return (
      <div
        className="font-display text-5xl md:text-6xl text-[#FFC107] drip-shadow"
        data-testid="countdown-value"
      >
        LIVE NOW
      </div>
    );
  }
  return (
    <div className="flex items-stretch gap-2 sm:gap-3" data-testid="countdown-value">
      {[
        { label: "DAYS", value: c.days },
        { label: "HRS", value: pad(c.hours) },
        { label: "MIN", value: pad(c.mins) },
        { label: "SEC", value: pad(c.secs) },
      ].map((cell) => (
        <div
          key={cell.label}
          className="flex flex-col items-center gap-1 min-w-[70px] sm:min-w-[90px] px-3 py-3 border border-[#FFC107]/50 bg-black/60"
        >
          <span className="font-display text-3xl sm:text-4xl text-[#FFC107] leading-none">
            {cell.value}
          </span>
          <span className="font-mono-tactical text-[10px] tracking-[0.3em] text-zinc-400">
            {cell.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Section 1 — Billboard / Hero
 * ------------------------------------------------------------------------- */
function BillboardHero() {
  return (
    <section
      className="relative hero-bg grain splatter overflow-hidden"
      data-testid="section-billboard-hero"
    >
      <div className="max-w-6xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-36 relative z-10">
        {/* Billboard panel */}
        <div className="billboard relative mx-auto fade-up">
          <div className="road-sticker absolute -top-4 left-4 text-[11px]">
            FMAA · EST. 2027
          </div>
          <div className="flex items-start gap-4 mb-6">
            <img
              src={BRAND_LOGO}
              alt="FMAA logo"
              className="w-14 h-14 md:w-16 md:h-16 object-contain"
              data-testid="hero-brand-logo"
            />
            <div className="leading-none">
              <div className="font-display text-5xl md:text-7xl tracking-tight">FMAA</div>
              <div className="font-mono-tactical text-[11px] md:text-xs tracking-[0.3em] text-[#FFC107] mt-2">
                FUELMEH ACROSS AMERICA
              </div>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95] drip-shadow">
            Your favorite
            <br />
            <span className="text-[#FF1493]">roadside</span>
            <br />
            distraction.
          </h1>

          <div className="mt-6 font-mono-tactical text-xs sm:text-sm tracking-[0.3em] text-zinc-400">
            50 STATES &nbsp;·&nbsp; NO REPEATS &nbsp;·&nbsp; ONE HELL OF A STORY
          </div>

          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <span className="road-sticker">WORTH THE STOP</span>
          </div>

          <div className="divider-x my-10" />

          {/* Launch countdown */}
          <div className="flex flex-col items-start gap-4">
            <div className="overline flex items-center gap-2">
              <span className="w-2 h-2 bg-[#FFC107] inline-block animate-pulse" />
              The journey begins January 11, 2027
            </div>
            <Countdown />
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="#the-journey"
              className="btn-blaze"
              data-testid="hero-follow-journey-btn"
            >
              FOLLOW THE JOURNEY <ArrowDown size={16} />
            </a>
            <a
              href="#origin-story"
              className="btn-ghost"
              data-testid="hero-what-is-fmaa-btn"
            >
              WHAT THE HELL IS FMAA? <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Section 2 — Mile 000 / Origin Story
 * ------------------------------------------------------------------------- */
function OriginSection() {
  return (
    <section
      className="relative bg-[#0A0A0A] grain"
      data-testid="section-origin-story"
      id="origin-story"
    >
      <SectionAnchor
        id="origin"
        mile="000"
        overline="The origin"
        title={<>THIS STARTED WITH A <span className="text-[#FF1493]">BLOW JOB.</span></>}
      />

      <div className="max-w-3xl mx-auto px-6 pb-20">
        <p className="font-script text-2xl sm:text-3xl text-zinc-200 italic mt-2">
          Well… technically, a question about one.
        </p>

        <div className="mt-8 space-y-5 text-zinc-300 leading-relaxed">
          <p className="font-mono-tactical text-xs uppercase tracking-widest text-[#FFC107]">
            One ridiculous question started it all:
          </p>
          <p className="text-lg sm:text-xl italic text-white">
            “How far across America could I get giving blow jobs for gas money?”
          </p>
          <p>That became <span className="font-graffiti text-[#FF1493]">BJAA: Blow Jobs Across America.</span></p>
          <p>Was it outrageous? Yep.</p>
          <p>Was it supposed to become a business? Absolutely fucking not.</p>
          <p>But underneath the joke was something real:</p>
          <p className="font-display text-2xl text-[#FFC107]">What if I just went?</p>
          <p>
            What if I stopped waiting for the perfect time, packed up the car, crossed America, met strangers,
            chased roadside weirdness and documented whatever happened along the way?
          </p>
          <p>The joke evolved. The vision didn't.</p>
          <p>
            BJAA became <span className="font-display text-xl text-white">FMAA: FuelMeh Across America.</span>
          </p>
          <p>And now we're going to find out how far one ridiculous idea can actually go.</p>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="font-display text-3xl sm:text-4xl text-[#FFC107] drip-shadow">
            EVERYTHING STARTS SOMEWHERE.
          </div>
          <Link
            to="/chat"
            className="btn-blaze"
            data-testid="origin-meet-booboo-btn"
          >
            MEET BOOBOO <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Section 3 — The Journey / Live Map (states tracker)
 * ------------------------------------------------------------------------- */
function JourneySection() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/journey/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const kpis = [
    { label: "STATES COMPLETED", value: stats ? `${stats.states_completed} / ${stats.states_total}` : "— / 50" },
    { label: "MILES TRAVELED", value: stats ? stats.miles_traveled.toLocaleString() : "0" },
    { label: "WORTH THE STOPS", value: stats ? stats.worth_the_stops_discovered : 0 },
    { label: "ROAD DAYS", value: stats ? stats.road_days : 0 },
  ];

  const currentState = stats?.current_state;

  return (
    <section
      className="relative bg-[#0d0d0d] topo-bg"
      data-testid="section-the-journey"
      id="the-journey"
    >
      <SectionAnchor
        id="journey"
        mile="500"
        overline="The journey"
        title={<>50 STATES. <span className="text-[#FF1493]">NO REPEATS.</span></>}
      />

      <div className="max-w-6xl mx-auto px-6 pb-20">
        {/* Current location banner */}
        <div className="flex flex-col sm:flex-row sm:items-stretch gap-3 mb-10 justify-center">
          <div className="highway-sign" data-testid="journey-current-location">
            <div className="text-[11px] tracking-widest opacity-80">CURRENT LOCATION</div>
            <div className="font-display text-2xl">
              {currentState ? `${currentState.name.toUpperCase()}` : "NOT ON THE ROAD YET"}
            </div>
          </div>
          <div className="highway-sign" style={{ background: "#273c75" }} data-testid="journey-launch-sign">
            <div className="text-[11px] tracking-widest opacity-80">LAUNCH</div>
            <div className="font-display text-2xl">01 · 11 · 2027</div>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {kpis.map((k) => (
            <div
              key={k.label}
              className="tac-card p-4 flex flex-col gap-1"
              data-testid={`journey-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="font-mono-tactical text-[10px] uppercase tracking-widest text-zinc-500">
                {k.label}
              </div>
              <div className="font-display text-3xl text-white leading-none">{k.value}</div>
            </div>
          ))}
        </div>

        {/* States grid */}
        <div className="tac-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <div className="overline">50 states · live tracker</div>
              <div className="font-display text-xl text-white mt-1">
                {stats && stats.states_completed > 0
                  ? "The road so far"
                  : "The whole country. Still untouched."}
              </div>
            </div>
            <StatesLegend />
          </div>
          <StatesGrid />
          <p className="mt-5 font-mono-tactical text-[11px] uppercase tracking-widest text-zinc-500 text-center">
            Completed states will become clickable journey archives.
          </p>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Section 4 — Worth the Stop
 * ------------------------------------------------------------------------- */
function WorthTheStopSection() {
  const [stops, setStops] = useState([]);

  useEffect(() => {
    api
      .get("/journey/worth-the-stops")
      .then((r) => setStops(r.data || []))
      .catch(() => setStops([]));
  }, []);

  const hasRealStops = stops.length > 0;

  return (
    <section
      className="relative bg-[#0A0A0A] grain"
      data-testid="section-worth-the-stop"
      id="worth-the-stop"
    >
      <SectionAnchor
        id="wts"
        mile="1200"
        overline="Worth the Stop"
        title="EVERY STATE GETS ONE."
      />
      <div className="max-w-5xl mx-auto px-6 pb-20">
        <div className="motel-key mb-8 inline-block">50 STATES · 50 STOPS · 50 STORIES</div>

        <p className="text-zinc-300 max-w-2xl mb-10 leading-relaxed">
          Every state gets exactly one official FMAA <strong>Worth the Stop</strong> — Cassandra's personal pick.
          Could be a roadside attraction, a weird landmark, a small-town restaurant, an artist, a stranger with a story.
          Not based on ratings. Based on whether it's actually worth pulling over for.
        </p>

        {hasRealStops ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stops.map((s) => (
              <div
                key={s.id}
                className="tac-card p-5"
                data-testid={`wts-card-${s.state_code}`}
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="road-sticker text-[11px]">{s.state_code} · OFFICIAL PICK</div>
                </div>
                <h3 className="font-display text-2xl text-white mb-1">{s.name}</h3>
                {s.city && (
                  <div className="font-mono-tactical text-xs text-zinc-500 uppercase tracking-widest mb-3">
                    {s.city}, {s.state_code}
                  </div>
                )}
                <p className="text-sm text-zinc-300">{s.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6 text-center py-10">
            <div className="stamp-not-yet">STILL OUT THERE SOMEWHERE</div>
            <p className="text-zinc-400 max-w-md">
              The first Worth the Stop will be chosen on the road.
              <br />Know somewhere worth pulling over for? Nominate it below.
            </p>
            <a
              href="#get-involved"
              className="btn-blaze"
              data-testid="wts-nominate-cta"
            >
              NOMINATE A STOP <ArrowDown size={16} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Section 4.5 — The Bucket List (BooBoo's running list, community-suggested)
 * ------------------------------------------------------------------------- */
function BucketListSection() {
  return (
    <section
      className="relative bg-[#0d0d0d] grain"
      data-testid="section-bucket-list"
      id="bucket-list"
    >
      <SectionAnchor
        id="bucket"
        mile="1700"
        overline="The bucket list"
        title={<>THINGS <span className="text-[#FFC107]">BOOBOO</span> HAS TO DO.</>}
      />
      <div className="max-w-5xl mx-auto px-6 pb-20">
        <p className="text-zinc-300 max-w-2xl mb-8 leading-relaxed">
          Experiences, weird detours, strange destinations. BooBoo keeps a running list of what has to
          happen on the road — some are planned, some are just rumours of a place, some are suggested
          by you. Items move through <strong>Not Yet</strong> → <strong>On the Route</strong> →
          <strong> Done</strong>, and completed ones hook back into the state they belong to.
        </p>

        <div className="mb-8">
          <BucketListItems limit={6} />
        </div>

        <BucketListSuggestionForm />
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Section 5 — Field Notes
 * ------------------------------------------------------------------------- */
function FieldNotesSection() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/posts").then((r) => setPosts((r.data || []).slice(0, 6))).catch(() => setPosts([]));
  }, []);

  const launchPassed = Date.now() >= new Date(LAUNCH_DATE).getTime();

  return (
    <section
      className="relative bg-[#0A0A0A] grain"
      data-testid="section-field-notes"
      id="field-notes"
    >
      <SectionAnchor
        id="field-notes"
        mile="2100"
        overline={launchPassed ? "From the road" : "Pre-trip log"}
        title={launchPassed ? "FIELD NOTES" : "FIELD NOTES · PRE-TRIP"}
      />
      <div className="max-w-6xl mx-auto px-6 pb-20">
        {posts.length === 0 ? (
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="stamp-not-yet">LOG STARTS SOON</div>
            <p className="text-zinc-400 max-w-md">
              Pre-trip entries will drop as FMAA prepares the Sentra and plans the route.
              Day 001 goes live January 11, 2027.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p, i) => (
                <article
                  key={p.id || i}
                  className="polaroid"
                  style={{ transform: `rotate(${(i % 2 === 0 ? -1.5 : 1.5)}deg)` }}
                  data-testid={`field-note-${p.id || i}`}
                >
                  <div className="polaroid-tape" />
                  {p.image_url ? (
                    <SafeImage className="polaroid-img" src={p.image_url} alt={p.title} />
                  ) : (
                    <div className="polaroid-img flex items-center justify-center bg-[#2a2a2a] text-zinc-500">
                      <MapPin size={28} />
                    </div>
                  )}
                  <div className="polaroid-caption">
                    {p.is_draft ? "DRAFT · " : ""}
                    {p.title}
                  </div>
                </article>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/feed"
                className="btn-ghost"
                data-testid="field-notes-see-all"
              >
                SEE ALL FIELD NOTES <ArrowRight size={16} />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Section 6 — Get Involved
 * ------------------------------------------------------------------------- */
function GetInvolvedSection() {
  return (
    <section
      className="relative bg-[#0d0d0d] grain splatter"
      data-testid="section-get-involved"
      id="get-involved"
    >
      <SectionAnchor
        id="involved"
        mile="3000"
        overline="Get involved"
        title={
          <>
            DON'T JUST FOLLOW THE ROAD.
            <br />
            <span className="text-[#FF1493]">BECOME PART OF IT.</span>
          </>
        }
      />
      <div className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <CommunityForm variant="on_route" />
          <CommunityForm variant="nominate_stop" />
          <CommunityForm variant="road_dare" />

          <div className="tac-card p-5 flex flex-col gap-3" data-testid="community-form-meet-booboo">
            <h3 className="font-display text-2xl uppercase tracking-tight leading-none">
              MEET BOOBOO
            </h3>
            <p className="text-xs font-mono-tactical text-zinc-500 mt-2 uppercase tracking-widest">
              Live meetups land on the route. RSVP to be the first to know.
            </p>
            <div className="flex flex-col gap-2 mt-4">
              <Link
                to="/meetups"
                className="btn-blaze self-start"
                data-testid="meet-booboo-meetups-btn"
              >
                SEE UPCOMING MEETUPS <ArrowRight size={16} />
              </Link>
              <Link
                to="/rsvp"
                className="btn-ghost self-start text-[11px]"
                data-testid="meet-booboo-rsvp-btn"
              >
                JOIN THE RSVP LIST <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Section 7 — Roadside Drops
 * ------------------------------------------------------------------------- */
function RoadsideDropsSection() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts((r.data || []).slice(0, 3)))
      .catch(() => setProducts([]));
  }, []);

  return (
    <section
      className="relative bg-[#0A0A0A] grain"
      data-testid="section-roadside-drops"
      id="roadside-drops"
    >
      <SectionAnchor
        id="drops"
        mile="3800"
        overline="Roadside drops"
        title="DROPS FROM THE ROAD"
      />
      <div className="max-w-6xl mx-auto px-6 pb-20">
        {products.length === 0 ? (
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="stamp-not-yet">INVENTORY LOADING</div>
            <p className="text-zinc-400 max-w-md">
              Limited drops, state-specific merch, FMAA artwork and the Tour Pass will land here.
              State drops unlock as the journey progresses.
            </p>
            <Link to="/tour-pass" className="btn-blaze" data-testid="drops-tour-pass-cta">
              <Crown size={16} /> GET THE TOUR PASS
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((p) => (
                <div key={p.id} className="tac-card overflow-hidden" data-testid={`drop-card-${p.id}`}>
                  {p.image_url ? (
                    <SafeImage
                      src={p.image_url}
                      alt={p.name}
                      className="w-full aspect-square object-cover"
                    />
                  ) : null}
                  <div className="p-4">
                    <h3 className="font-display text-xl text-white uppercase">{p.name}</h3>
                    <div className="font-mono-tactical text-xs text-zinc-500 uppercase tracking-widest mt-1">
                      ${Number(p.price).toFixed(0)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10 flex flex-wrap gap-3 justify-center">
              <Link to="/shop" className="btn-blaze" data-testid="drops-shop-btn">
                <ShoppingBag size={16} /> SHOP ALL DROPS
              </Link>
              <Link to="/tour-pass" className="btn-ghost" data-testid="drops-tour-pass-btn">
                <Crown size={16} /> TOUR PASS
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Section 8 — Fuel the Journey
 * ------------------------------------------------------------------------- */
function FuelSection() {
  return (
    <section
      className="relative bg-[#0d0d0d] grain"
      data-testid="section-fuel-the-journey"
      id="fuel-the-journey"
    >
      <SectionAnchor
        id="fuel"
        mile="4500"
        overline="Fuel the journey"
        title={<>KEEP THE <span className="text-[#FFC107]">SENTRA</span> MOVING.</>}
      />
      <div className="max-w-5xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "TOUR PASS",
              body: "30 days of VIP lounge, early drops, founding pin.",
              cta: "GET THE PASS",
              icon: <Crown size={18} />,
              to: "/tour-pass",
              testid: "fuel-tour-pass",
            },
            {
              title: "GAS MONEY",
              body: "Direct donations keep the Sentra on the road.",
              cta: "FUEL THE TANK",
              icon: <Fuel size={18} />,
              to: "/fuel",
              testid: "fuel-donate",
            },
            {
              title: "SPONSOR A CITY",
              body: "Brands can own a stop on the route.",
              cta: "SEE AVAILABLE SLOTS",
              icon: <MapPin size={18} />,
              to: "/press",
              testid: "fuel-sponsor",
            },
            {
              title: "PRESS & PARTNERSHIPS",
              body: "Features, collabs, podcast appearances, interviews.",
              cta: "GET IN TOUCH",
              icon: <Mail size={18} />,
              to: "/press",
              testid: "fuel-press",
            },
          ].map((row) => (
            <div
              key={row.title}
              className="tac-card p-5 flex flex-col gap-3"
              data-testid={`fuel-card-${row.testid}`}
            >
              <div className="flex items-center gap-2 text-[#FFC107]">
                {row.icon}
                <div className="font-display text-xl uppercase">{row.title}</div>
              </div>
              <p className="text-sm text-zinc-300">{row.body}</p>
              <Link
                to={row.to}
                className="btn-ghost self-start text-[11px] mt-auto"
                data-testid={`${row.testid}-btn`}
              >
                {row.cta} <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Section 9 — Bigger Than a Road Trip
 * ------------------------------------------------------------------------- */
function BiggerSection() {
  return (
    <section
      className="relative bg-gradient-to-b from-[#0A0A0A] via-[#140a1f] to-[#0A0A0A] grain"
      data-testid="section-bigger"
      id="bigger"
    >
      <SectionAnchor
        id="bigger-than"
        mile="4900"
        overline="The long road"
        title="THIS IS BIGGER THAN A ROAD TRIP."
      />
      <div className="max-w-3xl mx-auto px-6 pb-24 text-center">
        {/* Minimalist Flower of Life hint */}
        <div className="flex justify-center mb-10 opacity-60" aria-hidden="true">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
            {Array.from({ length: 7 }).map((_, i) => {
              const angle = (i / 6) * Math.PI * 2;
              const cx = 60 + (i === 6 ? 0 : Math.cos(angle) * 20);
              const cy = 60 + (i === 6 ? 0 : Math.sin(angle) * 20);
              return (
                <circle
                  key={i}
                  cx={cx}
                  cy={cy}
                  r="20"
                  stroke="#FFC107"
                  strokeWidth="0.8"
                  fill="none"
                  opacity="0.7"
                />
              );
            })}
          </svg>
        </div>

        <p className="font-display text-2xl sm:text-3xl text-white leading-snug">
          FMAA is proof that where you've been doesn't have to decide where you're going.
        </p>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3 justify-center">
          {["Healing", "Creativity", "Second chances", "Community", "Connection", "Freedom"].map((w) => (
            <div
              key={w}
              className="font-mono-tactical text-xs uppercase tracking-widest text-zinc-400 py-2 border-y border-white/5"
            >
              {w}
            </div>
          ))}
        </div>

        <div className="mt-12 flex items-center justify-center gap-2 text-[#FFC107]">
          <Sparkles size={16} />
          <span className="font-script text-xl">more to come</span>
          <Sparkles size={16} />
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Section 10 — The Horizon + Footer
 * ------------------------------------------------------------------------- */
function HorizonFooter() {
  const links = [
    { to: "/#the-journey", label: "JOURNEY", hash: true },
    { to: "/#worth-the-stop", label: "WORTH THE STOP", hash: true },
    { to: "/bucket-list", label: "BUCKET LIST" },
    { to: "/feed", label: "FIELD NOTES" },
    { to: "/meetups", label: "MEETUPS" },
    { to: "/shop", label: "SHOP" },
    { to: "/tour-pass", label: "TOUR PASS" },
    { to: "/lounge", label: "LOUNGE" },
    { to: "/fuel", label: "FUEL" },
    { to: "/chat", label: "BOOBOO AI" },
    { to: "/press", label: "PRESS" },
    { to: "/links", label: "LINKS" },
  ];
  const socials = [
    { href: "https://instagram.com/fuelmehnyc", label: "Instagram", icon: <Instagram size={16} /> },
    { href: "https://tiktok.com/@fuelmehnyc", label: "TikTok", icon: <Music2 size={16} /> },
    { href: "https://youtube.com/@fuelmeh", label: "YouTube", icon: <Youtube size={16} /> },
    { href: "https://fuelmeh.com", label: "Site", icon: <Globe size={16} /> },
    { href: "https://venmo.com/u/fuelmeh", label: "Venmo", icon: <DollarSign size={16} /> },
  ];

  return (
    <section
      className="relative bg-[#070707] grain overflow-hidden"
      data-testid="section-horizon"
      id="horizon"
    >
      <SectionAnchor
        id="horizon"
        mile="5000"
        overline="The horizon"
        title="NEXT STOP: EVERYWHERE."
      />

      <div className="max-w-6xl mx-auto px-6 pb-16">
        {/* Horizon road vanishing to a point */}
        <div className="flex items-center justify-center mb-14" aria-hidden="true">
          <svg width="100%" height="140" viewBox="0 0 400 140" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="fade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFC107" stopOpacity="0.0" />
                <stop offset="40%" stopColor="#FFC107" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#FFC107" stopOpacity="0" />
              </linearGradient>
            </defs>
            <polygon points="150,135 250,135 220,10 180,10" fill="#1a1a1a" />
            <line x1="200" y1="135" x2="200" y2="10" stroke="url(#fade)" strokeWidth="3" strokeDasharray="8 10" />
          </svg>
        </div>

        {/* Brand lockup */}
        <div className="text-center">
          <img src={BRAND_LOGO} alt="FMAA" className="w-14 h-14 object-contain mx-auto" />
          <div className="font-display text-5xl md:text-6xl mt-4 tracking-tight">FMAA</div>
          <div className="font-mono-tactical text-xs tracking-[0.3em] text-[#FFC107] mt-2">
            FUELMEH ACROSS AMERICA
          </div>
          <div className="font-script text-2xl text-zinc-300 mt-4">
            Your favorite roadside distraction.
          </div>
          <div className="road-sticker mt-6 inline-flex">WORTH THE STOP</div>
        </div>

        {/* Links row */}
        <nav className="mt-14 flex flex-wrap gap-x-6 gap-y-3 justify-center">
          {links.map((l) => {
            const testid = `horizon-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
            const cls =
              "font-mono-tactical text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-[#FF4D8B] transition-colors";
            if (l.hash) {
              return (
                <a key={l.to + l.label} href={l.to} className={cls} data-testid={testid}>
                  {l.label}
                </a>
              );
            }
            return (
              <Link key={l.to + l.label} to={l.to} className={cls} data-testid={testid}>
                {l.label}
              </Link>
            );
          })}
        </nav>

        {/* Socials */}
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          {socials.map((s) => (
            <a
              key={s.href}
              href={s.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-[#FF4D8B] text-zinc-300 hover:text-[#FF4D8B] text-xs transition-colors"
              data-testid={`horizon-social-${s.label.toLowerCase()}`}
            >
              {s.icon}
              {s.label}
            </a>
          ))}
        </div>

        {/* Legal / colophon */}
        <div className="mt-14 text-center font-mono-tactical text-[10px] tracking-widest text-zinc-600 uppercase">
          © {new Date().getFullYear()} FuelMeh · 50 states, no repeats · launching Jan 11, 2027
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  Page
 * ------------------------------------------------------------------------- */
export default function Landing() {
  return (
    <div className="bg-[#0A0A0A]" data-testid="landing-page">
      <ScrollMileIndicator />
      <BillboardHero />
      <RoadBetween heightClass="h-24" />
      <OriginSection />
      <RoadBetween heightClass="h-20" />
      <JourneySection />
      <RoadBetween heightClass="h-20" />
      <WorthTheStopSection />
      <RoadBetween heightClass="h-20" />
      <BucketListSection />
      <RoadBetween heightClass="h-20" />
      <FieldNotesSection />
      <RoadBetween heightClass="h-20" />
      <GetInvolvedSection />
      <RoadBetween heightClass="h-20" />
      <RoadsideDropsSection />
      <RoadBetween heightClass="h-20" />
      <FuelSection />
      <RoadBetween heightClass="h-20" />
      <BiggerSection />
      <RoadBetween heightClass="h-24" />
      <HorizonFooter />
    </div>
  );
}
